import { ComboBox, Label, SubmitButton, Switch } from "@xola/ui-kit";
import clsx from "clsx";
import { SecondaryButton } from "common";
import React from "react";
import { useMutation } from "react-query";
import { PurchaseRuleLink, XolaPartner, XolaPartnerConnectionType, XolaPartnerStatus } from "types";
import { boolean, object, string } from "yup";
import { ContentLayout } from "../../Layout/ContentLayout";
import { ProductTermsTable } from "./TermsTemplate/ProductTermsTable";
import { api } from "@/api/api";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { Loader } from "@/components/Loader";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";

interface UpdatePartnerPayload {
    connectionType?: XolaPartnerConnectionType;
    purchaseRuleLink?: { id: ID } | null;
    status?: XolaPartnerStatus;
    viewPurchases: boolean;
    editPurchases: boolean;
}

export const SellerTerms = ({ partner }: { partner: XolaPartner }) => {
    const invalidate = useInvalidate();

    const { data: termsTemplates, isLoading: isLoadingTermsTemplates } = useAutoFetchCollection<PurchaseRuleLink>(
        ["purchaseRuleLinks"],
        { "tags._id": "Affiliate", expand: "purchaseRule" },
    );

    const { mutateAsync: updatePartner, isLoading: isUpdatingPartner } = useMutation(
        async (payload: UpdatePartnerPayload) => {
            return api.patch(`/partners/${partner?.id}`, payload);
        },
    );

    const handleSubmit = async (values: SellerTermsFormValues) => {
        const connectionType = getConnectionTypeFromFormValues(values.sellerResells, values.partnerResells);
        const payload: UpdatePartnerPayload = {
            viewPurchases: values.viewPurchases ?? false,
            editPurchases: values.editPurchases ?? false,
        };

        if (!connectionType) {
            payload.status = XolaPartnerStatus.DISCONNECTED;
        } else if (connectionType !== partner.connectionType) {
            payload.connectionType = connectionType;
        }

        if (values.purchaseRuleLink !== partner.purchaseRuleLink?.id && values.purchaseRuleLink) {
            payload.purchaseRuleLink = { id: values.purchaseRuleLink };
        }

        if (!values.partnerResells) {
            payload.purchaseRuleLink = null;
        }

        await updatePartner(payload);
        invalidate("partners", partner?.id);
    };

    if (isLoadingTermsTemplates || !termsTemplates || !partner) {
        return <Loader />;
    }

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange={false}
            validationSchema={getValidationSchema()}
            initialValues={getInitialValues(partner)}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, handleReset, values, errors, setFieldValue, dirty }) => {
                return (
                    <ContentLayout isWide classNames={{ root: "!mb-0 pb-0" }}>
                        <div className="space-y-4 divide-y divide-gray-light pl-6">
                            <div className="flex space-x-14 py-12">
                                <div className="flex max-w-[320px] flex-col">
                                    <h3>Your Products & Terms</h3>
                                    <p className="text-gray-darker">
                                        Permission for this operator to sell your products according to your terms.
                                    </p>
                                </div>
                                <div className="w-1/2 pl-16">
                                    <div className="flex flex-col space-y-2">
                                        <span className="font-bold">Permissions</span>
                                        <div className="flex items-center">
                                            <Switch
                                                isDisabled
                                                isChecked={values.partnerResells}
                                                onChange={(value: boolean) => setFieldValue("partnerResells", value)}
                                            />
                                            <span className="pl-1">
                                                Allow {partner?.partnerSeller.name} to sell my products
                                            </span>
                                        </div>
                                        <div className="flex items-center">
                                            <Switch
                                                isChecked={values.viewPurchases ?? false}
                                                onChange={(value: boolean) => {
                                                    setFieldValue("viewPurchases", value);
                                                    if (!value) {
                                                        setFieldValue("editPurchases", false);
                                                    }
                                                }}
                                            />
                                            <span className="pl-1">
                                                Allow {partner?.partnerSeller.name} to view reservations they make
                                            </span>
                                        </div>
                                        <div className="flex items-center">
                                            <Switch
                                                isChecked={values.editPurchases ?? false}
                                                onChange={(value: boolean) => {
                                                    setFieldValue("editPurchases", value);
                                                    if (value) {
                                                        setFieldValue("viewPurchases", true);
                                                    }
                                                }}
                                            />
                                            <span className="pl-1">
                                                Allow {partner?.partnerSeller.name} to modify reservations they make
                                            </span>
                                        </div>
                                    </div>
                                    {values.partnerResells && (
                                        <div className="flex flex-col space-y-2 pt-2">
                                            <Label className={clsx(errors?.purchaseRuleLink && "!text-danger")}>
                                                Terms Template {errors.purchaseRuleLink}
                                            </Label>
                                            <ComboBox
                                                placeholder="Select Terms template"
                                                value={termsTemplates.find(
                                                    (template) => template.id === values.purchaseRuleLink,
                                                )}
                                                options={termsTemplates}
                                                getOptionValue={(option: PurchaseRuleLink) => option.id}
                                                getOptionLabel={(option: PurchaseRuleLink) => option.name}
                                                classNames={{
                                                    option: () => "no-translate",
                                                    singleValue: () => "no-translate",
                                                }}
                                                onChange={(template: PurchaseRuleLink) =>
                                                    setFieldValue("purchaseRuleLink", template.id)
                                                }
                                            />
                                        </div>
                                    )}
                                    {values.partnerResells && values.purchaseRuleLink ? (
                                        <div className="space-y-1 pt-6">
                                            <span className="font-bold">Products & Terms</span>
                                            {values.purchaseRuleLink && termsTemplates.length > 0 ? (
                                                <ProductTermsTable
                                                    purchaseRuleLink={values.purchaseRuleLink}
                                                    termsTemplates={termsTemplates}
                                                />
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        <ContentLayout.Footer>
                            <SubmitButton
                                disabled={(!values.sellerResells && !values.partnerResells) || !dirty}
                                isLoading={isUpdatingPartner}
                                type="submit"
                                onClick={handleSubmit}
                            >
                                Save
                            </SubmitButton>
                            <SecondaryButton onClick={handleReset}>Cancel</SecondaryButton>
                        </ContentLayout.Footer>
                    </ContentLayout>
                );
            }}
        </XolaFormik>
    );
};

interface SellerTermsFormValues {
    sellerResells: boolean;
    partnerResells: boolean;
    purchaseRuleLink?: string;
    viewPurchases: boolean;
    editPurchases: boolean;
}

const getInitialValues = (partner: XolaPartner): SellerTermsFormValues => {
    return {
        sellerResells: [XolaPartnerConnectionType.SUPPLIER, XolaPartnerConnectionType.SUPPLIER_RESELLER].includes(
            partner.connectionType,
        ),
        partnerResells: [XolaPartnerConnectionType.RESELLER, XolaPartnerConnectionType.SUPPLIER_RESELLER].includes(
            partner.connectionType,
        ),
        purchaseRuleLink: partner.purchaseRuleLink?.id,
        viewPurchases: partner?.viewPurchases ?? false,
        editPurchases: partner?.editPurchases ?? false,
    };
};

const getConnectionTypeFromFormValues = (sellerResells: boolean, partnerResells: boolean) => {
    if (sellerResells && partnerResells) {
        return XolaPartnerConnectionType.SUPPLIER_RESELLER;
    }

    if (sellerResells) {
        return XolaPartnerConnectionType.SUPPLIER;
    }

    if (partnerResells) {
        return XolaPartnerConnectionType.RESELLER;
    }

    // No one resell
    return "";
};

const getValidationSchema = () => {
    return object({
        sellerResells: boolean().required(),
        partnerResells: boolean().required(),
        purchaseRuleLink: string().when("partnerResells", {
            is: true,
            then: string().required("is required"),
            otherwise: string().notRequired(),
        }),
    });
};
